import React from 'react';
import { LayoutDashboardIcon, UsersIcon, UserIcon, CalendarIcon, BriefcaseIcon, ShieldIcon, StethoscopeIcon, ContactIcon, TrendingUpIcon, SettingsIcon, LogOutIcon, FileTextIcon, ClipboardListIcon, MessageSquareIcon, ChevronsLeftIcon, ChevronsRightIcon, XIcon, BrainIcon, PercentIcon, PackageIcon, UserCircleIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useData } from '../../contexts/DataContext';
import { ModulePermissions } from '../../types';
interface SidebarProps {
  currentModule: string;
  onModuleChange: (module: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onMobileClose?: () => void;
}
const menuGroups = [{
  label: 'Principal',
  items: [{
    id: 'dashboard',
    label: 'Panel de Control',
    icon: LayoutDashboardIcon,
    permission: 'dashboard' as keyof ModulePermissions
  }, {
    id: 'patients',
    label: 'Pacientes',
    icon: UsersIcon,
    permission: 'patients' as keyof ModulePermissions
  }, {
    id: 'patient360',
    label: 'Cliente 360',
    icon: UserCircleIcon,
    permission: 'patient360' as keyof ModulePermissions
  }, {
    id: 'appointments',
    label: 'Citas',
    icon: CalendarIcon,
    permission: 'appointments' as keyof ModulePermissions
  }, {
    id: 'clinical-history',
    label: 'Historial Clínico',
    icon: FileTextIcon,
    permission: 'clinicalHistory' as keyof ModulePermissions
  }]
}, {
  label: 'Clínica',
  items: [{
    id: 'doctors',
    label: 'Doctores',
    icon: UserIcon,
    permission: 'doctors' as keyof ModulePermissions
  }, {
    id: 'services',
    label: 'Servicios',
    icon: BriefcaseIcon,
    permission: 'services' as keyof ModulePermissions
  }, {
    id: 'products',
    label: 'Productos',
    icon: PackageIcon,
    permission: 'products' as keyof ModulePermissions
  }, {
    id: 'insurance',
    label: 'Seguros',
    icon: ShieldIcon,
    permission: 'insurance' as keyof ModulePermissions
  }, {
    id: 'specialties',
    label: 'Especialidades',
    icon: StethoscopeIcon,
    permission: 'specialties' as keyof ModulePermissions
  }]
}, {
  label: 'CRM',
  items: [{
    id: 'contacts',
    label: 'Contactos',
    icon: ContactIcon,
    permission: 'contacts' as keyof ModulePermissions
  }, {
    id: 'opportunities',
    label: 'Oportunidades',
    icon: TrendingUpIcon,
    permission: 'opportunities' as keyof ModulePermissions
  }, {
    id: 'follow-up-notes',
    label: 'Notas de Seguimiento',
    icon: MessageSquareIcon,
    permission: 'followUpNotes' as keyof ModulePermissions
  }, {
    id: 'commissions',
    label: 'Comisiones',
    icon: PercentIcon,
    permission: 'commissions' as keyof ModulePermissions
  }, {
    id: 'intelligence',
    label: 'Inteligencia',
    icon: BrainIcon,
    permission: 'intelligence' as keyof ModulePermissions
  }]
}, {
  label: 'Sistema',
  items: [{
    id: 'audit-log',
    label: 'Bitácora',
    icon: ClipboardListIcon,
    permission: 'auditLog' as keyof ModulePermissions
  }, {
    id: 'settings',
    label: 'Configuración',
    icon: SettingsIcon,
    permission: 'settings' as keyof ModulePermissions
  }]
}];
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/a741bfNsYBMGGZNxniTt4s/images.jpg';
export function Sidebar({
  currentModule,
  onModuleChange,
  isCollapsed,
  onToggleCollapse,
  onMobileClose
}: SidebarProps) {
  const {
    logout,
    user
  } = useAuth();
  const {
    canView
  } = usePermissions();
  const {
    isLoading
  } = useData();
  return <aside className={`h-screen bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700/50 flex flex-col overflow-y-auto overscroll-contain transition-all duration-300 ease-in-out shadow-xl md:shadow-none ${isCollapsed ? 'w-[68px]' : 'w-72 md:w-64'}`} style={{
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(156, 163, 175, 0.5) transparent'
  }} data-id="element-179">
      {/* Logo + close */}
      <div className="flex-shrink-0 p-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between min-h-[65px]" data-id="element-180">
        <button onClick={() => onModuleChange('dashboard')} className={`flex items-center gap-3 overflow-hidden hover:opacity-80 transition-opacity ${isCollapsed ? 'justify-center w-full' : ''}`} title="Ir al Panel de Control" data-id="element-181">
          <img src={LOGO_URL} alt="Savidna Logo" className="w-9 h-9 rounded-full object-cover flex-shrink-0 ring-2 ring-sky-100 dark:ring-sky-900" onError={e => {
          // Fallback: show initials if image fails to load
          e.currentTarget.style.display = 'none';
        }} data-id="element-182" />
          {!isCollapsed && <div className="min-w-0 text-left" data-id="element-183">
              <h1 className="text-base font-bold text-gray-900 dark:text-white truncate" data-id="element-184">
                Savidna
              </h1>
              <p className="text-xs text-gray-400 dark:text-gray-500 truncate" data-id="element-185">
                CRM Médico
              </p>
            </div>}
        </button>
        {onMobileClose && <button onClick={onMobileClose} className="md:hidden p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors flex-shrink-0" aria-label="Cerrar menú" data-id="element-186">
            <XIcon className="w-5 h-5" data-id="element-187" />
          </button>}
      </div>

      {/* User info pill — mobile only */}
      {!isCollapsed && user && <div className="flex-shrink-0 md:hidden mx-3 mt-3 px-3 py-2.5 bg-gradient-to-r from-sky-50 to-cyan-50 dark:from-sky-900/20 dark:to-cyan-900/20 rounded-xl border border-sky-100 dark:border-sky-800/30 flex items-center gap-2.5" data-id="element-188">
          <div className="w-8 h-8 rounded-full bg-sky-100 dark:bg-sky-900 flex items-center justify-center flex-shrink-0" data-id="element-189">
            {user.avatar ? <img src={user.avatar} alt={user.name} className="w-8 h-8 rounded-full object-cover" data-id="element-190" /> : <span className="text-sky-600 dark:text-sky-400 font-bold text-sm" data-id="element-191">
                {user.name?.charAt(0).toUpperCase()}
              </span>}
          </div>
          <div className="min-w-0" data-id="element-192">
            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate" data-id="element-193">
              {user.name}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 truncate" data-id="element-194">
              {user.email}
            </p>
          </div>
        </div>}

      {/* Collapse toggle — desktop only */}
      <button onClick={onToggleCollapse} className="flex-shrink-0 hidden md:flex ml-3 my-2 items-center justify-center w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors" title={isCollapsed ? 'Expandir menú' : 'Colapsar menú'} data-id="element-195">
        {isCollapsed ? <ChevronsRightIcon className="w-4 h-4" data-id="element-196" /> : <ChevronsLeftIcon className="w-4 h-4" data-id="element-197" />}
      </button>

      {/* Navigation — entire aside scrolls, so no internal scroll here */}
      <nav className="flex-shrink-0 py-2" data-id="element-198">
        {menuGroups.map(group => {
        const visibleItems = group.items.filter(item => user && canView(item.permission));
        if (visibleItems.length === 0) return null;
        return <div key={group.label} className="mb-1" data-id="element-199">
              {!isCollapsed && <p className="px-4 pt-3 pb-1 text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-600" data-id="element-200">
                  {group.label}
                </p>}
              {isCollapsed && <div className="mx-3 my-1 h-px bg-gray-100 dark:bg-gray-800" data-id="element-201" />}
              <ul className="space-y-0.5 px-2" data-id="element-202">
                {visibleItems.map(item => {
              const Icon = item.icon;
              const isActive = currentModule === item.id;
              return <li key={item.id} data-id="element-203">
                      <button onClick={() => onModuleChange(item.id)} title={isCollapsed ? item.label : undefined} className={`
                          w-full flex items-center gap-3 rounded-xl
                          text-sm font-medium transition-all duration-150 relative group
                          ${isCollapsed ? 'px-0 py-2.5 justify-center' : 'px-3 py-2.5'}
                          ${isActive ? 'bg-gradient-to-r from-sky-50 to-cyan-50 dark:from-sky-900/30 dark:to-cyan-900/20 text-sky-600 dark:text-sky-400 shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/70 hover:text-gray-900 dark:hover:text-gray-200'}
                        `} data-id="element-204">
                        {isActive && <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-sky-500 dark:bg-sky-400 rounded-r-full" data-id="element-205" />}
                        <Icon className={`w-[18px] h-[18px] flex-shrink-0 transition-transform ${isActive ? 'text-sky-500 dark:text-sky-400' : ''}`} data-id="element-206" />
                        {!isCollapsed && <span className="truncate" data-id="element-207">{item.label}</span>}
                      </button>
                    </li>;
            })}
              </ul>
            </div>;
      })}
      </nav>

      {/* Logout */}
      <div className="flex-shrink-0 p-3 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900" style={{
      marginBottom: 'env(safe-area-inset-bottom)'
    }} data-id="element-208">
        <button onClick={logout} title={isCollapsed ? 'Cerrar Sesión' : undefined} className={`w-full flex items-center gap-3 rounded-xl text-sm font-medium text-red-500 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors ${isCollapsed ? 'px-0 py-2.5 justify-center' : 'px-3 py-2.5'}`} data-id="element-209">
          <LogOutIcon className="w-[18px] h-[18px] flex-shrink-0" data-id="element-210" />
          {!isCollapsed && <span data-id="element-211">Cerrar Sesión</span>}
        </button>
      </div>
      {/* Mobile spacer to clear BottomNav (h-14 = 56px) */}
      <div className="flex-shrink-0 md:hidden h-14" aria-hidden="true" data-id="element-212" />
    </aside>;
}